import {
    AddLinkDocument,
    GetAllLinksDocument,
    OrderBy,
    UpdateLinkDocument,
} from 'types/db';
import { Route } from 'vue-router';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import cloneDeep from 'lodash.clonedeep';
import { Notify } from 'src/notifications';
import { v4 as uuidv4 } from 'uuid';
import { EntityTypeMap } from '../../types/entity';
import { validateLink } from './link.validations';
import {
    type EntityModifyParameters,
    type ExtendedEntityParameters,
} from 'src/components/EntityDetail';
import type { ValidationResult } from 'types';
import { accessor } from 'src/store';

export type Link = EntityTypeMap['link'];

export const sampleLink: Link = {
    id: uuidv4(),
    group_name: '',
    name: '',
    description: '',
    url: '',
    shared: true,
};

function upperCaseFirstCharacter (str: string): string {
    return `${str}`.charAt(0).toUpperCase() + `${str}`.slice(1);
}

export async function getLinkEntity (to: Route): Promise<{ entity: Link }> {
    if (to.query.mode === 'create') {
        return { entity: { ...cloneDeep(sampleLink), id: uuidv4() } };
    }
    const { links } = await gqlClient.request({
        document: GetAllLinksDocument,
        variables: {
            filter: {
                id: { _eq: to.params.linkId },
            },
            colsToOrderBy: {
                group_name: OrderBy.Asc,
                name: OrderBy.Asc,
            },
        },
    });

    if (links[0]) {
        return {
            entity: links[0],
        };
    } else {
        const err = new Error('Unknown Link');
        Notify.error(err);
        throw err;
    }
}

export async function saveLinkEntity (payload: ExtendedEntityParameters<'link'>): Promise<ValidationResult> {
    const entity = payload.entity;
    // i know we said we didnt want to use accessor in the validation functions, but since this is using the auth stuff, we can assume it will be available anywhere
    const validationResult = validateLink(entity, accessor.auth.currentUser?.id || 'unknown');
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    try {
        const data = await gqlClient.request({
            document: UpdateLinkDocument,
            variables: {
                linkId: entity.id,
                link: {
                    group_name: upperCaseFirstCharacter(entity.group_name),
                    name: upperCaseFirstCharacter(entity.name),
                    description: entity.description,
                    url: entity.url,
                    shared: entity.shared,
                },
            },
        });
        if (Number(data?.updateResults?.affected_rows) !== 1) {
            return {
                status: 'error',
                errorMessage: 'Failed to save Link',
            };
        }

        return {
            status: 'success',
            newRoute: {
                name: 'home',
            },
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Link: "${JSON.stringify(err)}"`,
        };
    }
}

export async function createLinkEntity (payload: EntityModifyParameters<'link'>): Promise<ValidationResult> {
    const entity = payload.entity;
    entity.group_name = upperCaseFirstCharacter(entity.group_name);
    entity.name = upperCaseFirstCharacter(entity.name);
    // i know we said we didnt want to use accessor in the validation functions, but since this is using the auth stuff, we can assume it will be available anywhere
    const validationResult = validateLink(entity, accessor.auth.currentUser?.id || 'unknown');
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: AddLinkDocument,
            variables: {
                link: [ entity ],
            },
        });
        return {
            status: 'success',
            newRoute: {
                name: 'home',
            },
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Link: "${JSON.stringify(err)}"`,
        };
    }
}
